import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { TaskType } from "@common/types/taskType";

const TRANSACTION_TASK_ENTITY_LOCKED_TOOLTIP =
    "Transactions-based tasks cannot change entity";
const FORM_1099_TASK_ENTITY_LOCKED_TOOLTIP =
    "1099 review tasks cannot change entity";
const COUNTERPARTIES_TASK_ENTITY_LOCKED_TOOLTIP =
    "Counterparties tasks cannot change entity";
const BILLS_TASK_ENTITY_LOCKED_TOOLTIP = "Bills tasks can't change entity";
const INVOICES_TASK_ENTITY_LOCKED_TOOLTIP =
    "Invoices tasks can't change entity";
const FINANCIAL_DOCUMENTS_TASK_ENTITY_LOCKED_TOOLTIP =
    "Financial documents tasks cannot change entity";
const SCHEDULE_REVIEW_TASK_ENTITY_LOCKED_TOOLTIP =
    "Schedule review tasks cannot change entity";
const INTEGRATION_COA_MAPPING_TASK_ENTITY_LOCKED_TOOLTIP =
    "Integration account mapping tasks cannot change entity";

export const SCHEDULE_RULE_SUGGESTED_TASK_TITLE =
    "Review rule-suggested schedules";
export const MILESTONE_REVENUE_RECOGNITION_REVIEW_TASK_TITLE =
    "Review milestone-based revenue recognition";

/**
 * What a sticky task pins in its live view filters: `transactions` pins
 * `transactionIds`, `counterparties` pins `counterpartiesIds`, `bills` pins
 * `billIds`, `invoices` pins `invoiceIds`, `financialDocuments` pins
 * `financialDocumentIds`. A new
 * directory-backed type adds a kind here and a matching branch in
 * TaskStickyViewInternalService.
 */
export type StickyMembershipKind =
    | "transactions"
    | "counterparties"
    | "bills"
    | "invoices"
    | "financialDocuments";

interface TaskTypeDefinition {
    label: string | null;
    isTemplateEligible: boolean;
    isWorkspaceWorkflow: boolean;
    isSystemManaged: boolean;
    isScheduleReview: boolean;
    isHiddenAtOrgLevel: boolean;
    isSticky: boolean;
    stickyMembership: StickyMembershipKind | null;
    hasSeparateChatPanel: boolean;
    entityLockedTooltip: string | null;
    requiredFeature: PREMIUM_FEATURES | null;
}

type TaskTypeFlag = {
    [K in keyof TaskTypeDefinition]: TaskTypeDefinition[K] extends boolean
        ? K
        : never;
}[keyof TaskTypeDefinition];

const DEFAULTS = {
    isTemplateEligible: false,
    isWorkspaceWorkflow: false,
    isSystemManaged: false,
    isScheduleReview: false,
    isHiddenAtOrgLevel: false,
    isSticky: false,
    stickyMembership: null,
    hasSeparateChatPanel: false,
    entityLockedTooltip: null,
    requiredFeature: null,
} as const;

/**
 * Single source of truth for per-task-type behavior. Every TaskType must have
 * an entry with an explicit `label` (null when the type is never listed by
 * name). Flags default to the plain case; set only what differs. Lists and
 * predicates used across backend, frontend and agent tools derive from here.
 * Adding a new type also needs: the DB enum migration, a view-config schema in
 * close-template-task-view-config.schema.ts, and body/island renderers in the
 * frontend task body maps. Invariants are covered by taskTypeDefinitions.spec.ts.
 */
export const TASK_TYPE_DEFINITIONS = {
    [TaskType.FILE]: { ...DEFAULTS, label: "File" },
    [TaskType.TRANSACTION]: {
        ...DEFAULTS,
        label: "Transaction",
        hasSeparateChatPanel: true,
    },
    [TaskType.TRANSACTIONS]: {
        ...DEFAULTS,
        label: "Transactions",
        isTemplateEligible: true,
        isWorkspaceWorkflow: true,
        isSticky: true,
        stickyMembership: "transactions",
        hasSeparateChatPanel: true,
        entityLockedTooltip: TRANSACTION_TASK_ENTITY_LOCKED_TOOLTIP,
    },
    [TaskType.QUESTION]: {
        ...DEFAULTS,
        label: "Question",
        isTemplateEligible: true,
        isWorkspaceWorkflow: true,
    },
    [TaskType.INVITE_ACCOUNTANT]: {
        ...DEFAULTS,
        label: "Invite accountant",
        isHiddenAtOrgLevel: true,
    },
    [TaskType.RECEIPT_PHONE_NUMBER]: {
        ...DEFAULTS,
        label: "Receipt phone number",
        isHiddenAtOrgLevel: true,
    },
    [TaskType.CONNECTION_BROKEN]: {
        ...DEFAULTS,
        label: "Connection broken",
        hasSeparateChatPanel: true,
    },
    [TaskType.PLAID_NEW_ACCOUNTS_AVAILABLE]: {
        ...DEFAULTS,
        label: "Plaid: new accounts",
        hasSeparateChatPanel: true,
    },
    [TaskType.PLAID_STATEMENTS_REFRESH]: {
        ...DEFAULTS,
        label: "Plaid: statements refresh",
        hasSeparateChatPanel: true,
    },
    [TaskType.MAP_CHART_OF_ACCOUNTS]: {
        ...DEFAULTS,
        label: "Map chart of accounts",
    },
    [TaskType.CONNECT_ACCOUNT]: {
        ...DEFAULTS,
        label: "Connect account",
        hasSeparateChatPanel: true,
    },
    [TaskType.MAP_MIGRATED_DATA]: {
        ...DEFAULTS,
        label: null,
        hasSeparateChatPanel: true,
    },
    [TaskType.PROFIT_LOSS_ACTIVATION]: {
        ...DEFAULTS,
        label: "P&L activation",
        hasSeparateChatPanel: true,
    },
    [TaskType.PROFIT_LOSS]: {
        ...DEFAULTS,
        label: "Profit & loss",
        isTemplateEligible: true,
        isWorkspaceWorkflow: true,
        hasSeparateChatPanel: true,
    },
    [TaskType.BALANCE_SHEET]: {
        ...DEFAULTS,
        label: "Balance sheet",
        isTemplateEligible: true,
        isWorkspaceWorkflow: true,
        hasSeparateChatPanel: true,
    },
    [TaskType.DOCUMENT_RETRIEVAL]: {
        ...DEFAULTS,
        label: "Upload statements",
        isTemplateEligible: true,
        isWorkspaceWorkflow: true,
        hasSeparateChatPanel: true,
    },
    [TaskType.GET_DOCUMENTS]: {
        ...DEFAULTS,
        label: "Upload documents",
        isTemplateEligible: true,
        isWorkspaceWorkflow: true,
        hasSeparateChatPanel: true,
    },
    [TaskType.ACCRUAL_REVIEW]: {
        ...DEFAULTS,
        label: "Accrual review",
        isTemplateEligible: true,
        hasSeparateChatPanel: true,
        requiredFeature: PREMIUM_FEATURES.ACCRUAL_LEDGER,
    },
    [TaskType.RECONCILIATION]: {
        ...DEFAULTS,
        label: "Reconciliation",
        isTemplateEligible: true,
        hasSeparateChatPanel: true,
        requiredFeature: PREMIUM_FEATURES.RECONCILIATION,
    },
    [TaskType.FILE_MIGRATION_UPLOAD]: {
        ...DEFAULTS,
        label: null,
        hasSeparateChatPanel: true,
    },
    [TaskType.XERO_FILE_UPLOAD]: { ...DEFAULTS, label: null },
    [TaskType.QBDT_FILE_UPLOAD]: { ...DEFAULTS, label: null },
    [TaskType.TRANSACTION_REVIEW]: {
        ...DEFAULTS,
        label: "Transactions review",
        isTemplateEligible: true,
        isSticky: true,
        stickyMembership: "transactions",
        hasSeparateChatPanel: true,
        entityLockedTooltip: TRANSACTION_TASK_ENTITY_LOCKED_TOOLTIP,
    },
    [TaskType.FORM_1099]: {
        ...DEFAULTS,
        label: "1099 review",
        isTemplateEligible: true,
        isSticky: true,
        stickyMembership: "counterparties",
        hasSeparateChatPanel: true,
        entityLockedTooltip: FORM_1099_TASK_ENTITY_LOCKED_TOOLTIP,
    },
    [TaskType.COUNTERPARTIES]: {
        ...DEFAULTS,
        label: "Counterparties",
        isTemplateEligible: true,
        isWorkspaceWorkflow: true,
        isSticky: true,
        stickyMembership: "counterparties",
        hasSeparateChatPanel: true,
        entityLockedTooltip: COUNTERPARTIES_TASK_ENTITY_LOCKED_TOOLTIP,
    },
    [TaskType.BILLS]: {
        ...DEFAULTS,
        label: "Bills",
        isTemplateEligible: true,
        isWorkspaceWorkflow: true,
        isSticky: true,
        stickyMembership: "bills",
        hasSeparateChatPanel: true,
        entityLockedTooltip: BILLS_TASK_ENTITY_LOCKED_TOOLTIP,
    },
    [TaskType.INVOICES]: {
        ...DEFAULTS,
        label: "Invoices",
        isTemplateEligible: true,
        isWorkspaceWorkflow: true,
        isSticky: true,
        stickyMembership: "invoices",
        hasSeparateChatPanel: true,
        entityLockedTooltip: INVOICES_TASK_ENTITY_LOCKED_TOOLTIP,
        requiredFeature: PREMIUM_FEATURES.INVOICES,
    },
    [TaskType.FINANCIAL_DOCUMENTS]: {
        ...DEFAULTS,
        label: "Financial documents",
        isTemplateEligible: true,
        isWorkspaceWorkflow: true,
        isSticky: true,
        stickyMembership: "financialDocuments",
        hasSeparateChatPanel: true,
        entityLockedTooltip: FINANCIAL_DOCUMENTS_TASK_ENTITY_LOCKED_TOOLTIP,
    },
    [TaskType.SCHEDULE_RULE_SUGGESTED_REVIEW]: {
        ...DEFAULTS,
        label: SCHEDULE_RULE_SUGGESTED_TASK_TITLE,
        isScheduleReview: true,
        hasSeparateChatPanel: true,
        entityLockedTooltip: SCHEDULE_REVIEW_TASK_ENTITY_LOCKED_TOOLTIP,
        requiredFeature: PREMIUM_FEATURES.ACCRUAL_LEDGER,
    },
    [TaskType.SCHEDULE_SUSPICIOUS_TRANSACTION_REVIEW]: {
        ...DEFAULTS,
        label: "Review suspicious schedule transactions",
        isScheduleReview: true,
        hasSeparateChatPanel: true,
        entityLockedTooltip: SCHEDULE_REVIEW_TASK_ENTITY_LOCKED_TOOLTIP,
        requiredFeature: PREMIUM_FEATURES.ACCRUAL_LEDGER,
    },
    [TaskType.REVENUE_RECOGNITION_REVIEW]: {
        ...DEFAULTS,
        label: "Review schedule recognition",
        isScheduleReview: true,
        hasSeparateChatPanel: true,
        entityLockedTooltip: SCHEDULE_REVIEW_TASK_ENTITY_LOCKED_TOOLTIP,
        requiredFeature: PREMIUM_FEATURES.ACCRUAL_LEDGER,
    },
    [TaskType.SCHEDULE_SOURCE_CHANGE_REVIEW]: {
        ...DEFAULTS,
        label: "Review schedule source changes",
        isScheduleReview: true,
        hasSeparateChatPanel: true,
        entityLockedTooltip: SCHEDULE_REVIEW_TASK_ENTITY_LOCKED_TOOLTIP,
        requiredFeature: PREMIUM_FEATURES.ACCRUAL_LEDGER,
    },
    [TaskType.MILESTONE_REVENUE_RECOGNITION_REVIEW]: {
        ...DEFAULTS,
        label: MILESTONE_REVENUE_RECOGNITION_REVIEW_TASK_TITLE,
        isScheduleReview: true,
        isSystemManaged: true,
        hasSeparateChatPanel: true,
        entityLockedTooltip: SCHEDULE_REVIEW_TASK_ENTITY_LOCKED_TOOLTIP,
        requiredFeature: PREMIUM_FEATURES.ACCRUAL_LEDGER,
    },
    [TaskType.INTEGRATION_COA_MAPPING]: {
        ...DEFAULTS,
        label: "Map integration transaction types",
        hasSeparateChatPanel: true,
        entityLockedTooltip: INTEGRATION_COA_MAPPING_TASK_ENTITY_LOCKED_TOOLTIP,
    },
} as const satisfies Record<TaskType, TaskTypeDefinition>;

type TaskTypeWithFlag<F extends TaskTypeFlag> = {
    [T in TaskType]: (typeof TASK_TYPE_DEFINITIONS)[T][F] extends true
        ? T
        : never;
}[TaskType];

export type SystemManagedTaskType = TaskTypeWithFlag<"isSystemManaged">;
export type ScheduleReviewTaskType = TaskTypeWithFlag<"isScheduleReview">;

const ALL_TASK_TYPES: readonly TaskType[] = Object.values(TaskType);

function isNonEmptyList<T>(items: readonly T[]): items is readonly [T, ...T[]] {
    return items.length > 0;
}

export const isSystemManagedTaskType = (
    type: TaskType,
): type is SystemManagedTaskType => TASK_TYPE_DEFINITIONS[type].isSystemManaged;

export const isScheduleReviewTaskType = (
    type: TaskType,
): type is ScheduleReviewTaskType =>
    TASK_TYPE_DEFINITIONS[type].isScheduleReview;

export const SYSTEM_MANAGED_TASK_TYPES: readonly SystemManagedTaskType[] =
    ALL_TASK_TYPES.filter(isSystemManagedTaskType);

export const SCHEDULE_REVIEW_TASK_TYPES: readonly ScheduleReviewTaskType[] =
    ALL_TASK_TYPES.filter(isScheduleReviewTaskType);

const templateEligibleTaskTypes = ALL_TASK_TYPES.filter(
    (type) => TASK_TYPE_DEFINITIONS[type].isTemplateEligible,
);

if (!isNonEmptyList(templateEligibleTaskTypes)) {
    throw new Error("At least one task type must be template eligible");
}

export const TEMPLATE_ELIGIBLE_TASK_TYPES = templateEligibleTaskTypes;

export const WORKSPACE_WORKFLOW_TASK_TYPES: readonly TaskType[] =
    ALL_TASK_TYPES.filter(
        (type) => TASK_TYPE_DEFINITIONS[type].isWorkspaceWorkflow,
    );

export const getTaskTypeLabel = (type: TaskType): string | null =>
    TASK_TYPE_DEFINITIONS[type].label;

export const isStickyTaskType = (type: TaskType): boolean =>
    TASK_TYPE_DEFINITIONS[type].isSticky;

export const getStickyMembershipKind = (
    type: TaskType,
): StickyMembershipKind | null => TASK_TYPE_DEFINITIONS[type].stickyMembership;

export const isHiddenAtOrgLevelTaskType = (type: TaskType): boolean =>
    TASK_TYPE_DEFINITIONS[type].isHiddenAtOrgLevel;

export const hasSeparateChatPanel = (type: TaskType): boolean =>
    TASK_TYPE_DEFINITIONS[type].hasSeparateChatPanel;

export const isEntityLockedTaskType = (type: TaskType): boolean =>
    TASK_TYPE_DEFINITIONS[type].entityLockedTooltip !== null;

export const getEntityLockedTooltip = (type: TaskType): string =>
    TASK_TYPE_DEFINITIONS[type].entityLockedTooltip ??
    TRANSACTION_TASK_ENTITY_LOCKED_TOOLTIP;
